import { borderRadius, color, Icon, space, typography } from '@primer-io/goat'
import Clickable from 'components/Clickable'
import { AnimatePresence, motion } from 'framer-motion'
import React, { createContext, useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import useFirstRender from '../../hooks/useFirstRender'
import { normalisePath } from '../../utils/prefix'
import { defaultSpring } from '../../utils/transitions'
import { SDK } from './sdk'
import { SECONDARY_MENU_WIDTH } from './data'
import CamelCaseWrap from 'components/CamelCaseWrap'
import { ChevronDown } from '@primer-io/goat-icons'

const pathContext = createContext('')

///////////////////////////////////////////
// Separator
///////////////////////////////////////////

const Separator = styled.div`
	background-color: ${color('gray-200')};
	height: 1px;
	padding: 0 16px;
	transition: all 0.3s linear;
`

///////////////////////////////////////////
// Item
///////////////////////////////////////////

const ItemTitle = styled(Clickable)`
	${typography('body-medium')};
	background-color: ${p => (!p.expandable && p.active ? color('gray-200') : 'transparent')};
	border-radius: ${borderRadius('medium')};
	color: ${p => color(p.active ? 'black' : 'gray-700')};
	cursor: pointer;
	display: flex;
	font-weight: ${p => (p.active ? '600' : '400')};
	gap: ${space(2)};
	margin: 0 ${space(2.5)};
	padding: ${space(2)};
	padding-left: ${space(3.5)};
	place-items: center;
	text-align: initial;
	transition: background-color 0.2s ease;
	word-break: break-word;

	&:hover {
		background-color: ${p => color(!p.expandable && p.active ? 'gray-200' : 'gray-100')};
	}

	&:focus {
		outline: 2px solid #005ecc;
		outline-offset: -2px;

		&:not(:focus-visible) {
			outline: none;
		}
	}

	${p =>
		p.expandable &&
		css`
			&:not(:first-child) {
				margin-top: ${space(2)};
			}
		`}
`

const IconWrapper = styled.div`
	margin: ${space(-0.5)} 0;
	margin-left: auto;
	transform: rotate(${p => (p.isCollapsed ? 0 : -180)}deg);
	transition: transform 0.3s ease;
`

const ItemExpandIcon = styled(Icon).attrs({
	color: 'gray-700',
})``

const ItemTitleImgContainer = styled.div`
	height: 24px;
	width: 24px;
`

const ItemTitleImg = styled.img`
	width: 100%;
	height: 100%;
	border-radius: 4px;
	object-fit: contain;
`

const ItemSubContainer = styled.div`
	padding-left: ${p => space(p.hasTitleImg ? 8 : 3)};
`

const Expandable = ({ children, isFirstRender }) => (
	<motion.div
		style={{ overflowY: 'hidden', overflowX: 'inerit' }}
		initial={!isFirstRender && { opacity: 0, height: 0, y: -10 }}
		animate={{ opacity: 1, height: 'auto', y: 0 }}
		exit={{ opacity: 0, height: 0, y: -10 }}
		transition={defaultSpring}
	>
		{children}
	</motion.div>
)

/** @param {{ items: unknown[], path: string, url: string }} */
const matches = ({ items, path, url }) => {
	url = normalisePath(url)

	if (!items?.length) return path === url

	const parts = path.split('/')
	return url.split('/').every((item, i) => item === parts[i])
}

const Item = ({ active, isFirstRender, items, onClick, title, titleImg, url }) => {
	const path = useContext(pathContext)
	active ??= matches({ items, path, url })
	const [isCollapsed, setIsCollapsed] = useState(!active)

	useEffect(() => {
		setIsCollapsed(!active)
	}, [active])

	const isExpandable = !!items?.length

	return (
		<>
			<ItemTitle
				active={active}
				expandable={isExpandable}
				items={items}
				onClick={e => {
					setIsCollapsed(!isCollapsed)
					onClick?.(e)
				}}
				to={url}
			>
				{titleImg && (
					<ItemTitleImgContainer>
						<ItemTitleImg src={titleImg} />
					</ItemTitleImgContainer>
				)}

				<CamelCaseWrap text={title} />
				{isExpandable && (
					<IconWrapper isCollapsed={isCollapsed}>
						<Icon as={ChevronDown} color='gray-700' size='medium' />
					</IconWrapper>
				)}
			</ItemTitle>
			<AnimatePresence>
				{isExpandable && !isCollapsed && (
					<Expandable isFirstRender={isFirstRender}>
						<ItemSubContainer hasTitleImg={!!titleImg}>
							<MenuItems margin menuItems={items} onClick={onClick} />
						</ItemSubContainer>
					</Expandable>
				)}
			</AnimatePresence>
		</>
	)
}

const Title = styled.div`
	${typography('label')}
	color: ${color('gray-900')};
	padding: ${space(2)} ${space(6)};

	&:not(:first-child) {
		margin-top: ${space(6)};
	}
`

const SmallText = styled.div`
	${typography('body-medium')};
	color: ${color('gray-500')};
	padding: 6px 16px 6px 24px;
`

///////////////////////////////////////////
// Section
///////////////////////////////////////////

const SectionRoot = styled.div`
	padding: ${space(4)} 0;

	&:first-child {
		padding-top: 0;
	}
`

const SectionTitle = styled.div`
	${typography('label')}
	letter-spacing: 0.15px;
	padding: 0 24px 6px;
`

const Section = ({ title, items, onClick }) => (
	<SectionRoot>
		{title && <SectionTitle>{title}</SectionTitle>}
		<MenuItems margin menuItems={items} onClick={onClick} />
	</SectionRoot>
)

///////////////////////////////////////////
// Root
///////////////////////////////////////////

const Root = styled.div`
	height: 100%;
	overflow: hidden auto;
	padding-bottom: ${space(6)};
	width: ${SECONDARY_MENU_WIDTH}px;
`

const Header = styled.div`
	${typography('header-small')}
	font-weight: 600;
	padding: 24px;
	padding-bottom: 0;
`

const MenuItem = ({ item, margin, onClick }) => {
	const firstRender = useFirstRender()

	switch (item.type) {
		case 'ITEM':
		case 'GROUP':
			return <Item {...item} isFirstRender={firstRender} onClick={(e, i) => onClick?.(e, i ?? item)} />
		case 'SECTION':
			return <Section {...item} onClick={onClick} />
		case 'SEPARATOR':
			return <Separator style={margin && { margin: '12px 24px' }} />
		case 'SMALL_TEXT':
			return <SmallText>{item.smallText}</SmallText>
		case 'TITLE':
			return <Title>{item.title}</Title>
		default:
			return null
	}
}

const MenuItems = ({ margin, menuItems, onClick }) =>
	menuItems.map((item, i) => (
		<MenuItem key={item.title ?? `${item.type}-${i}`} item={item} margin={margin} onClick={onClick} />
	))

export const SecondaryMenuItems = ({ location, margin, menuItems, onClick }) => (
	<pathContext.Provider value={normalisePath(location.pathname)}>
		{menuItems.map((item, i) => (
			<MenuItem key={item.title ?? `${item.type}-${i}`} item={item} margin={margin} onClick={onClick} />
		))}
	</pathContext.Provider>
)

export default function SecondaryMenu({ location, menuItems, onClick, platform, sdkItems, title, version }) {
	const isSdk = sdkItems && platform

	return (
		<Root>
			<Header>{title}</Header>
			{isSdk && <SDK platforms={sdkItems} platform={platform} version={version} />}
			<SecondaryMenuItems location={location} menuItems={menuItems} onClick={onClick} />
		</Root>
	)
}
